// El marco de la columna 2 de la ventana «Santi» (propuesta §5.1, §5.3, P1/P3/P9), igual para un contrato, el chat y
// «Pedir algo»: la cabecera (título y una línea; a la derecha, lo del contrato y después lo de la ventana: raya, «Tu
// agente» y ✕), el cuerpo, la caja de escribir abajo, la línea flotante de «Hecho»/«Cancelar» sobre la caja, y el cajón
// del agente: empuja (escritorio) o tapa de la cabecera a la caja (iPad).
// 04-11 (ADR D-037): con el lenguaje de Apple de la oficina, como VentanaChat — cabecera de 64 sobre el gris de ventana,
// título 17 y línea 14 (la propuesta pedía 72 para que cupieran 18/15; con la letra de la oficina caben en 64, y así
// la cabecera queda a la altura de la del cajón), botones en cápsula de `ui/`.
import { Bot, ChevronLeft, X } from 'lucide-react';
import type { ReactNode } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { Boton, BotonIcono } from '../ui';
import { useHablaComoPau } from './store';

export function MarcoColumna({
  cabecera,
  cuerpo,
  pie,
  linea,
  cajon,
  cajonEncima = false,
  className,
}: {
  cabecera: ReactNode;
  cuerpo: ReactNode;
  pie: ReactNode;
  /** La línea flotante de «Hecho» / «Cancelar» (abajo, sobre la caja). */
  linea?: ReactNode;
  /** El cajón «Tu agente», si está abierto. */
  cajon?: ReactNode;
  /** iPad y menos: el cajón tapa la conversación (de la cabecera a la caja) en vez de empujarla. */
  cajonEncima?: boolean;
  className?: string;
}) {
  return (
    <section className={cn('flex min-h-0 min-w-0 flex-col bg-oficina-elevada', className)} data-testid="charla-santi" aria-label={t('oficinaV2.pelotas.ventana.titulo')}>
      {cabecera}
      <div className="flex min-h-0 flex-1">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="relative min-h-0 flex-1">
            {cuerpo}
            {linea ? (
              <div className="pointer-events-none absolute inset-x-0 bottom-4 z-10 flex justify-center px-6">
                <div className="pointer-events-auto w-full max-w-[800px]">{linea}</div>
              </div>
            ) : null}
            {cajon && cajonEncima ? <div className="absolute inset-y-0 right-0 z-20 w-[380px] max-w-full shadow-o-hoja max-md:w-full">{cajon}</div> : null}
          </div>
          {pie}
        </div>
        {cajon && !cajonEncima ? <div className="w-[380px] shrink-0">{cajon}</div> : null}
      </div>
    </section>
  );
}

/** La cabecera de la columna 2: título y línea 2 a la izquierda; lo del contrato (`acciones`) y lo de la ventana a la derecha. */
export function CabeceraSanti({
  titulo,
  sub,
  subCompleta,
  acciones,
  ancha,
  cajonAbierto,
  alCajon,
  alCerrar,
  alVerLista,
}: {
  titulo: string;
  sub: ReactNode;
  /** La línea 2 entera (el `title` cuando no cabe toda). */
  subCompleta?: string;
  acciones?: ReactNode;
  /** Escritorio (≥ 1280): «Tu agente» con su nombre; iPad, solo el icono. */
  ancha: boolean;
  cajonAbierto: boolean;
  alCajon: () => void;
  alCerrar: () => void;
  /** Móvil: volver a la lista. */
  alVerLista?: () => void;
}) {
  const comoPau = useHablaComoPau();
  const nombreAgente = t(comoPau ? 'oficinaV2.pelotas.agente.titulo' : 'oficinaV2.pelotas.agente.dePau');
  return (
    <header className="flex h-16 shrink-0 items-center gap-2 border-b border-oficina-linea bg-oficina-elevada pl-6 pr-3 max-md:pl-2" data-testid="charla-cabecera">
      {alVerLista ? <BotonIcono icono={ChevronLeft} etiqueta={t('oficinaV2.pelotas.ventana.verLista')} onClick={alVerLista} /> : null}
      <div className="mr-2 min-w-0 flex-1">
        <h2 className="truncate text-o-titulo3 font-semibold text-oficina-texto" data-testid="charla-titulo">
          {titulo}
        </h2>
        <p className="mt-0.5 truncate text-o-texto text-oficina-texto2" title={subCompleta} data-testid="charla-sub">
          {sub}
        </p>
      </div>
      {acciones}
      <span className="mx-1 h-6 w-px shrink-0 bg-white/10" aria-hidden />
      {ancha ? (
        // Fantasma en gris (el oro es solo «Enviar»); abierto, en el gris de lo pulsado, como un botón de alternar de Apple.
        <Boton
          variante="fantasma"
          icono={Bot}
          onClick={alCajon}
          aria-pressed={cajonAbierto}
          className={cajonAbierto ? 'bg-oficina-control text-oficina-texto hover:bg-oficina-pulsado' : 'text-oficina-texto2 hover:text-oficina-texto'}
          data-testid="boton-tu-agente"
        >
          {nombreAgente}
        </Boton>
      ) : (
        <BotonIcono icono={Bot} etiqueta={nombreAgente} onClick={alCajon} aria-pressed={cajonAbierto} activo={cajonAbierto} data-testid="boton-tu-agente" />
      )}
      <BotonIcono icono={X} etiqueta={t('oficinaV2.pelotas.ventana.cerrar')} onClick={alCerrar} data-testid="cerrar-ventana" />
    </header>
  );
}
